import React, { useEffect, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, fmtDate, panelGet, useAdminGet } from '../../kit';
import { Badge, Drawer, EmailPreview, fmtFrom } from '../emailKit';
import { sendDesignToCampaign } from '../designs/types';
import { SaveAsTemplate } from '../content/TemplatePicker';
import { SourceContent, fromSource } from '../content/types';
import { LinkRankTable } from '../links/LinkClicks';
import { tagCell, useTagOptions } from '../../tags/shared';
import { AutomatedToggle, EngTags, EngagementNote } from '../dashboard/shared';
import {
  ATTR_LABELS, ATTR_OPTIONS, FunnelSteps, Step, describeEcho, labelize, num,
} from './shared';

/**
 * Every campaign ever sent: name, when, by whom, to whom (the stored filters
 * in words), what, and how far it got — the funnel numbers beside the
 * delivery counts. The drawer is the whole record: funnel, envelope, the
 * content as sent (placeholders unfilled) and every recipient's path.
 * The automatic lifecycle mails share the ledger and are listed underneath.
 * Opened / clicked are people's (Backend/email_engagement.py); auto opens and
 * bot clicks are their own columns, each recipient is tagged, and "Include
 * automated" (automated=1) shows the raw numbers.
 */

const statusTone = (s: string) => (s === 'running' ? 'amber' : s === 'stalled' || s === 'failed' ? 'red' : 'green');
const flag = (on: any, at?: string) => (on
  ? <span className="text-green-800" title={at ? fmtDate(at) : undefined}>✓{at ? ` ${fmtDate(at)}` : ''}</span>
  : <span className="text-grayscale-1100">—</span>);

export const CampaignHistory: React.FC<{
  open: string | null; onOpen: (id: string | null) => void; tplName: (k: string) => string;
}> = ({ open, onOpen, tplName }) => {
  const [attr, setAttr] = useState('30');
  const [auto, setAuto] = useState<string | null>(null);
  const [automated, setAutomated] = useState(false);
  const q = useAdminGet('email', 'target_sends',
    { funnel: '1', attr_days: attr, ...(automated ? { automated: '1' } : {}) }, [attr, automated]);
  const tagOpts = useTagOptions();
  const tagLabel = (k: string) => tagOpts.tagByKey[k]?.label || k;
  const h: any = q.data || {};
  const batches: any[] = h.batches || [];
  const automatic: any[] = h.automatic || [];
  const running = batches.some(b => b.status === 'running' && Number(b.pending || 0) > 0);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => q.reload(), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);
  const rows = batches.map(b => ({
    ...b,
    campaign: b.name || b.subject || b.send_id,
    content: b.content_mode === 'custom' ? 'Written for this send' : b.content_mode === 'design' ? 'Design' : tplName(b.template_key),
    audience: describeEcho(b.filters, tagLabel, tplName).join(' · '),
    opened: b.funnel?.opened, clicked: b.funnel?.clicked, visited: b.funnel?.visited,
    signed_up: b.funnel?.signed_up, trials: b.funnel?.trial, bounced: b.funnel?.bounced,
    auto_opened: b.funnel?.auto_opened, bot_clicked: b.funnel?.bot_clicked,
  }));

  return (
    <div className="flex flex-col gap-4">
      <Section title="Campaigns"
        right={<div className="flex items-center gap-2">
          {running && <Badge tone="amber">sending — refreshing every few seconds</Badge>}
          <AutomatedToggle value={automated} onChange={setAutomated} />
          <span className="text-xs text-grayscale-1100">Sign-ups and trials counted</span>
          <Select value={attr} onChange={setAttr} options={ATTR_OPTIONS} labels={ATTR_LABELS} />
          <Btn small onClick={q.reload}>Refresh</Btn>
        </div>}>
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading && !q.data ? <Loading /> : (
          <>
            {h.ledger_ready === false && (
              <div className="text-xs text-grayscale-1100">Nothing has been sent through the campaign ledger yet.</div>
            )}
            <DataTable rows={rows} csvName="email_campaigns" defaultSort="-created_at" maxHeight="36rem"
              columns={['created_at', 'campaign', 'content', 'audience', 'sent_by', 'queued', 'sent', 'failed',
                'bounced', 'opened', 'clicked', 'auto_opened', 'bot_clicked', 'visited', 'signed_up', 'trials', 'status']}
              labels={{ created_at: 'Sent', sent_by: 'By', queued: 'Recipients', signed_up: 'Signed up',
                auto_opened: 'Auto opens', bot_clicked: 'Bot clicks' }}
              render={{
                campaign: r => <span className="font-medium" title={r.subject}>{r.campaign}</span>,
                audience: r => <span className="text-xs text-grayscale-1200" title={r.audience}>{r.audience}</span>,
                failed: r => <span className={Number(r.failed) > 0 ? 'text-red-700 font-medium' : ''}>{num(r.failed)}</span>,
                status: r => <Badge tone={statusTone(r.status)}>{r.status || '—'}</Badge>,
              }}
              actions={r => <Btn small onClick={() => onOpen(String(r.send_id))}>Open</Btn>}
              empty="No campaigns yet." />
            <div className="text-[11px] text-grayscale-1100">
              Opened and clicked come from SendGrid's events{automated ? ', every open and click counted'
                : ' — people only: auto opens = opened only by machines (instant reads, Apple MPP, scanners), bot clicks = clicked only by link scanners'};
              visited = a site visit through one of the campaign's tracked links; signed up = first sign-in after the
              mail; trials = a trial started after it.
            </div>
          </>
        )}
      </Section>

      {automatic.length > 0 && (
        <Section title="Sent automatically (trial started / ending / lapsed …)">
          <DataTable rows={automatic} columns={['template_key', 'sent', 'failed', 'last_at']}
            labels={{ template_key: 'Template', last_at: 'Last sent' }}
            render={{ template_key: r => <span>{tplName(r.template_key)}</span> }}
            actions={r => <Btn small onClick={() => setAuto(String(r.template_key))}>Recipients</Btn>} />
        </Section>
      )}

      {open && <CampaignDrawer sendId={open} attr={attr} automated={automated} tplName={tplName} tagLabel={tagLabel}
        onClose={() => { onOpen(null); q.reload(); }} />}
      {auto && <AutoSendsDrawer tplKey={auto} tplName={tplName} onClose={() => setAuto(null)} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// One campaign
// ---------------------------------------------------------------------------
const CampaignDrawer: React.FC<{
  sendId: string; attr: string; automated?: boolean; tplName: (k: string) => string; tagLabel: (k: string) => string;
  onClose: () => void;
}> = ({ sendId, attr, automated, tplName, tagLabel, onClose }) => {
  const q = useAdminGet('email', 'target_funnel',
    { send_id: sendId, attr_days: attr, ...(automated ? { automated: '1' } : {}) }, [sendId, attr, automated]);
  const [status, setStatus] = useState('');
  const [showContent, setShowContent] = useState(false);
  // "Save as a template": the campaign as SourceContent (email_library.library_source).
  const [asTpl, setAsTpl] = useState<SourceContent | null>(null);
  const [tplNote, setTplNote] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [tplBusy, setTplBusy] = useState(false);
  const saveAsTemplate = async () => {
    setTplBusy(true); setTplNote(null);
    const { data, error } = await panelGet('email', 'library_source', { kind: 'campaign', ref: sendId });
    setTplBusy(false);
    if (error) { setTplNote({ kind: 'error', text: error }); return; }
    setAsTpl(data as SourceContent);
  };
  const d: any = q.data || {};
  const b: any = d.batch || null;
  const counts: Record<string, number> = d.counts || {};
  const steps: Step[] = d.steps || [];
  const recipients: any[] = d.recipients || [];
  const live = b?.status === 'running' && Number(counts.queued || 0) > 0;
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => q.reload(), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);
  const shown = status ? recipients.filter(r => r.status === status) : recipients;
  const cc: string[] = Array.isArray(b?.cc) ? b.cc : [];
  const bcc: string[] = Array.isArray(b?.bcc) ? b.bcc : [];
  const sources = d.sources || {};

  return (
    <Drawer title={b?.name || b?.subject || `Campaign ${sendId}`}
      subtitle={b ? `${b.content_mode === 'custom' ? 'Written for this send' : b.content_mode === 'design' ? 'Built with the design builder' : tplName(b.template_key)} · `
        + `${fmtDate(b.created_at)}${b.sent_by ? ` · by ${b.sent_by}` : ''} · ${sendId}` : undefined}
      onClose={onClose}>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading && !q.data ? <Loading /> : b && (
        <>
          <Section title="Funnel"
            right={<span className="text-xs text-grayscale-1100">sign-ups and trials {ATTR_LABELS[attr]} the send</span>}>
            <FunnelSteps steps={steps} />
            {d.auto && typeof d.auto === 'object' && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-grayscale-1100">
                <span>Auto opens only <b className="text-grayscale-1300">{num(d.auto.auto_opened)}</b></span>
                <span>Bot clicks only <b className="text-grayscale-1300">{num(d.auto.bot_clicked)}</b></span>
                <span>with an instant read {num(d.auto.instant_open)} · Apple MPP {num(d.auto.machine_open)} ·
                  scanner open {num(d.auto.scanner_open)} · bot click {num(d.auto.bot_click)}</span>
              </div>
            )}
            <EngagementNote eng={d.engagement} automated={!!automated} />
            {(sources.email_events === false || sources.site_visits === false) && (
              <div className="text-[11px] text-amber-800">
                {sources.email_events === false && 'No e-mail events table here, so delivered / opened / clicked stay 0. '}
                {sources.site_visits === false && 'No site-visit table here, so visited stays 0.'}
              </div>
            )}
          </Section>
          <Section title="Links clicked"
            right={<span className="text-xs text-grayscale-1100">every link in this e-mail is tracked</span>}>
            <LinkRankTable rows={d.links || []}
              empty="Nobody has clicked a link in this campaign yet." />
          </Section>
          <KPIRow>
            {(['queued', 'sent', 'failed', 'skipped', 'bounced'] as const).map(s => (
              <KPI key={s} label={labelize(s)} value={num(counts[s])}
                onClick={() => setStatus(v => (v === s ? '' : s))} active={status === s} />
            ))}
          </KPIRow>
          {live && <div className="text-xs text-grayscale-1100">Still sending — refreshing every few seconds.</div>}
          {b.error && <Msg kind="error">{b.error}</Msg>}
          <div className="text-xs text-grayscale-1200 flex flex-col gap-1 bg-white rounded-lg border border-grayscale-300 p-3">
            <div><b>Audience:</b> {describeEcho(b.filters, tagLabel, tplName).join(' · ')}</div>
            <div><b>From:</b> {fmtFrom(b.from_name, b.from_email) || "the template's own"}
              {b.reply_to ? <> · <b>Reply-To:</b> {b.reply_to}</> : null}
              {cc.length > 0 && <> · <b>CC:</b> {cc.join(', ')}</>}
              {bcc.length > 0 && <> · <b>BCC:</b> {bcc.join(', ')}</>}
            </div>
            <div><b>Subject:</b> {b.subject}</div>
            {b.placeholders && typeof b.placeholders === 'object' && (() => {
              // What the placeholders became in this campaign (admin_email_targeting
              // target_send: fallbacks, site values, how many were missing each).
              const ph: any = b.placeholders;
              const eff: Record<string, string> = ph.effective || {};
              const miss: Record<string, number> = ph.missing || {};
              const ov: Record<string, string> = ph.overrides || {};
              const used = Object.keys(miss).filter(k => miss[k] > 0);
              if (!used.length && !Object.keys(ov).length && !Object.keys(ph.fallbacks || {}).length) return null;
              return (
                <div>
                  <b>Placeholders:</b>{' '}
                  {used.map((k, i) => (
                    <span key={k}>{i > 0 && ' · '}{`{${k}}`} missing for {num(miss[k])} → {eff[k] ? `“${eff[k]}”` : 'left blank'}
                      {(ph.fallbacks || {})[k] !== undefined ? ' (this campaign’s)' : ''}</span>
                  ))}
                  {Object.entries(ov).map(([k, v], i) => (
                    <span key={k}>{(used.length || i > 0) && ' · '}{`{${k}}`} = {String(v)}</span>
                  ))}
                </div>
              );
            })()}
            {b.design && typeof b.design === 'object' && (
              <button type="button" onClick={() => sendDesignToCampaign(b.design, b.name || b.subject || 'Past campaign', b.subject || '')}
                className="self-start text-xs underline underline-offset-2 text-primary-og-600">
                Use this design again (opens a new campaign with it)
              </button>
            )}
            {(b.html || b.text) && (
              <button type="button" onClick={() => setShowContent(v => !v)}
                className="self-start text-xs underline underline-offset-2 text-primary-og-600">
                {showContent ? 'Hide the content' : 'Show the content as sent (placeholders unfilled)'}
              </button>
            )}
            <button type="button" onClick={saveAsTemplate} disabled={tplBusy}
              className="self-start text-xs underline underline-offset-2 text-primary-og-600 disabled:opacity-50">
              {tplBusy ? 'Loading…' : 'Save as a template (for Campaigns and MEMAC)'}
            </button>
            {tplNote && <Msg kind={tplNote.kind}>{tplNote.text}</Msg>}
          </div>
          {showContent && (
            <EmailPreview subject={b.subject || ''} html={b.html || ''} text={b.text || undefined}
              from={fmtFrom(b.from_name, b.from_email)} replyTo={b.reply_to || undefined} height={420} />
          )}
          <DataTable rows={shown} csvName={`campaign_${sendId}`} searchable maxHeight="60vh"
            columns={['email', 'name', 'status', 'delivered', 'opened_at', 'clicked_at', 'automation', 'links', 'visited_at',
              'signed_up_at', 'trial_at', 'lifecycle', 'error']}
            unsortable={['automation']}
            labels={{ opened_at: 'Opened', clicked_at: 'Clicked', links: 'Links clicked', visited_at: 'Visited',
              signed_up_at: 'Signed up', trial_at: 'Trial', lifecycle: 'Now', automation: 'Automated' }}
            render={{
              status: r => <Badge tone={r.status === 'sent' ? 'green' : r.status === 'failed' || r.status === 'bounced'
                ? 'red' : r.status === 'skipped' ? 'grey' : 'amber'}>{r.status || '—'}</Badge>,
              delivered: r => flag(r.delivered),
              opened_at: r => flag(r.opened, r.opened_at),
              clicked_at: r => flag(r.clicked, r.clicked_at),
              automation: r => (Array.isArray(r.eng_tags) && r.eng_tags.length
                ? <span className="inline-flex gap-1 flex-wrap"><EngTags row={r} /></span>
                : <span className="text-grayscale-1100">—</span>),
              links: r => (Array.isArray(r.links) && r.links.length
                ? <span className="text-xs" title={r.links.join('\n')}>{r.links[0]}{r.links.length > 1 ? ` +${r.links.length - 1}` : ''}</span>
                : <span className="text-grayscale-1100">—</span>),
              visited_at: r => flag(r.visited, r.visited_at),
              signed_up_at: r => flag(r.signed_up, r.signed_up_at),
              trial_at: r => flag(r.trial, r.trial_at),
              lifecycle: tagCell,
            }}
            empty={status ? `No ${status} recipients.` : 'No recipients recorded.'} />
        </>
      )}
      {asTpl && (() => {
        const s = fromSource(asTpl);
        return (
          <SaveAsTemplate content={s.content} envelope={s.envelope} fallbacks={s.fallbacks} origin="campaigns"
            source={s.source} defaultName={b?.name || b?.subject || asTpl.name} defaultCategory={s.category}
            title="Save this campaign as a template"
            note={<>It goes to Email Center → Saved templates, where new campaigns and MEMAC triggers can use it.
              {asTpl.warnings.length > 0 && <> {asTpl.warnings.join(' ')}</>}
              {(asTpl.notes || []).length > 0 && <> {(asTpl.notes || []).join(' ')}.</>}</>}
            onClose={() => setAsTpl(null)}
            onSaved={(t, w) => {
              setAsTpl(null);
              setTplNote({ kind: 'ok', text: `Saved as “${t.name}” in Saved templates.${w.length ? ` ${w.join(' ')}` : ''}` });
            }} />
        );
      })()}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// The automatic sends of one template (no batch, so no funnel record)
// ---------------------------------------------------------------------------
const AutoSendsDrawer: React.FC<{ tplKey: string; tplName: (k: string) => string; onClose: () => void }> =
  ({ tplKey, tplName, onClose }) => {
  const q = useAdminGet('email', 'target_send_detail', { send_id: 'auto', key: tplKey }, [tplKey]);
  const d: any = q.data || {};
  const rows: any[] = d.rows || [];
  return (
    <Drawer title={`Automatic sends · ${tplName(tplKey)}`} onClose={onClose}>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading && !rows.length ? <Loading /> : (
        <DataTable rows={rows} csvName={`automatic_${tplKey}`} searchable maxHeight="70vh"
          columns={['email', 'name', 'status', 'error', 'sendgrid_message_id', 'updated_at']}
          labels={{ sendgrid_message_id: 'SendGrid id', updated_at: 'At' }}
          render={{
            status: r => <Badge tone={r.status === 'sent' ? 'green' : r.status === 'failed' ? 'red' : 'amber'}>{r.status || '—'}</Badge>,
          }}
          empty="No automatic sends recorded." />
      )}
      {rows.length >= 2000 && <div className="text-[11px] text-grayscale-1100">Showing the newest 2,000.</div>}
    </Drawer>
  );
};
